import {createContext, useContext, useEffect, useRef, useState} from "react";
import {get, getSessionToken, post, postFile, setSessionToken} from "./utils/http.client";
import {missingLocalization} from "./utils/localization.utils";
import {namespacedTypeDescriptionKey, legacyTypeDescriptionKey} from "./utils/descriptionKeys";
import {apiBindings} from "./utils/constants";
import {useIntegrator} from "./integrator.context";
import {MetadataCollection} from "./commands/metadataValue.command";
import {ImageMaskMetadata, Metadata, RemoteConstantsModel, SoundTypes} from "./models/remoteConstants.model";
import {ChangeConstantsCommand} from "./commands/changeConstants.command";
import {ChangeLocalizationValuesCommand} from "./commands/changeLocalizationValues.command";
import {AccessLevel} from "./models/accessLevel.model";
import {getUnitAttributeDependencyWarnings} from "./utils/dependencies";
import {bakeImagePixelMultipliers, computePixelMultiplier} from "./utils/exportTransform";
import {AttributeManifest, AttributeManifestEntry, AttributeEntityKind, FieldConstraint} from "./models/attributeManifest.model";
import {applyFieldConstraints, applyParamConstraints} from "./utils/clamp";
import {NavTarget} from "./utils/navTarget";
import {findMetadata} from "./utils/metadata.utils";
import {NormalisedBounds, opaqueBounds, readImagePixels} from "./utils/opaqueBounds";

// Read a just-selected image file's natural pixel dimensions locally (no
// network) so we can store them and compute a correct pixelMultiplier. Returns
// undefined if the file can't be decoded as an image.
async function readImageDimensions(file: File): Promise<{width: number; height: number} | undefined> {
    try {
        const bitmap = await createImageBitmap(file);
        const dimensions = {width: bitmap.width, height: bitmap.height};
        bitmap.close();
        return dimensions;
    } catch {
        return undefined;
    }
}

// Where the subject sits within a just-selected file, recorded so the client can
// frame the art as a UI icon without scanning it (see opaqueBounds.ts and
// RemoteConstants.GetTrimmedImage). Local, so no network and no CORS. Undefined
// when the file can't be read or holds nothing visible — the client then derives
// the box itself, so this failing costs nothing but a runtime scan.
async function readImageTrim(file: File): Promise<NormalisedBounds | undefined> {
    const pixels = await readImagePixels(file);
    return pixels === undefined ? undefined : opaqueBounds(pixels);
}

export interface AddMetadataData {
    metadata: Metadata;
    collection: MetadataCollection;
}

export interface NotificationData {
    title: string;
    message: string;
    edit?: boolean;
    headerRight?: React.ReactNode;
    // Optional list of pre-formatted strings rendered as a collapsible
    // section below the message. Used by the export flow to surface
    // inactive-reference warnings without flooding the notification body.
    warnings?: string[];
}

export interface ConfirmPrompt {
    title: string;
    body: React.ReactNode;
    confirmText: string;
    danger?: boolean;
}

// Multi-choice variant of ConfirmPrompt: resolves with the chosen option's
// value, or undefined when dismissed (click outside).
export interface ChoicePrompt {
    title: string;
    body: React.ReactNode;
    options: { text: string; value: string; danger?: boolean }[];
}

interface AppProviderProps {
    children: React.ReactNode;
}

interface AppContextData {
    authenticated: boolean;
    accessLevel: AccessLevel;
    // This user's PlayFab id and display name, from /me or login (empty until
    // the deployed backend returns them). The id addresses the user's own
    // game sessions in Live Theme Preview; the name is for display.
    playFabId: string;
    displayName: string;
    loading: boolean;
    setLoading(value: boolean): void;
    markdownFile: string | undefined;
    setMarkdownFile(value: string | undefined): void;
    metadataValue: AddMetadataData | undefined;
    setMetadataValue(value: AddMetadataData | undefined): void;
    showIds: boolean;
    setShowIds(value: boolean): void;
    viewJson: boolean;
    setViewJson(value: boolean): void;
    viewBranch: boolean;
    setViewBranch(value: boolean): void;
    notification: NotificationData | undefined;
    setNotification(value: NotificationData | undefined): void;
    unit: string | undefined;
    setUnit(value: string | undefined): void;
    login(email: string, password: string): Promise<boolean>;
    register(name: string, email: string, password: string): Promise<boolean>;
    checkSession(): Promise<boolean>;
    logout(): void;
    exportRemoteConstants(prod: boolean): Promise<boolean>;
    addBranch(branch: string): Promise<boolean>;
    removeBranch(branch: string): Promise<boolean>;
    uploadFile(id: string, file: File): Promise<boolean>;
    // Attach a team-colour mask to an image: uploads the greyscale PNG and points
    // images[id].mask at it, carrying the settings it was derived from so the
    // dialog can reopen on them. Any mask it displaces goes to the archive.
    uploadMask(id: string, file: File, settings: Omit<ImageMaskMetadata, 'fileName'>): Promise<boolean>;
    // Drop an image's team colouring entirely (the blob goes to the archive).
    removeMask(id: string): void;
    // URL of an image's mask blob, or undefined when it has none.
    getMaskUrl(id: string): string | undefined;
    removeBackground(id: string): Promise<Blob | undefined>;
    getImageUrl(id: string): string;
    getSoundUrl(id: string): string | undefined;
    getLocalizationValue(key: string): string;
    getTypeDescriptionKey(uid: string, typeId: string): string;
    getEntityOptions(): { label: string; options: string[] }[];
    // Schema lookups backed by attribute-manifest.json. Undefined entry => the
    // attribute isn't in the manifest (legacy / non-manifest collection), so
    // callers fall back to free-form editing.
    getAttributeManifestEntry(id: string, collection: MetadataCollection): AttributeManifestEntry | undefined;
    // Numeric range for a fixed model field, keyed by data path (e.g.
    // "unit.properties.attack"). Undefined when the field isn't annotated (or the
    // manifest predates constraints) — callers then leave the input unclamped.
    getFieldConstraint(key: string): FieldConstraint | undefined;
    // All manifest entries for a collection (Units/Buildings). Empty for
    // non-manifest collections or before the manifest has loaded.
    getAttributeManifestEntries(collection: MetadataCollection): AttributeManifestEntry[];
    // True when `id` matches a Hidden entry in the unit-attribute manifest.
    // Used by the Attributes tab to split Unit Types out of Unit Attributes
    // and by MetadataElement to suppress the parameter-add / delete affordances.
    isHiddenUnitAttribute(id: string): boolean;
    // Raw attribute manifest. Exposed so the editor can trigger a one-shot
    // hidden-attribute sync once both branch and manifest are loaded — see
    // SyncHiddenUnitAttributesCommand. Undefined until the manifest fetch
    // resolves (or fails).
    attributeManifest: AttributeManifest | undefined;
    getEntityOptionsForKind(kind: AttributeEntityKind): { label: string; options: string[] }[];
    localizationFocusKey: string | undefined;
    navigateToLocalization(key: string): void;
    focusLocalizationKey(key: string): void;
    setOnNavigateToLocalization(callback: ((key: string) => void) | undefined): void;
    // Stored as objects (not bare strings) so each call produces a fresh
    // reference — lets consumer effects re-fire even when navigating to the
    // same entity twice in a row.
    focusedUnit: { id: string } | undefined;
    focusedBuilding: { id: string } | undefined;
    navigateToUnit(id: string): void;
    navigateToBuilding(id: string): void;
    setOnNavigateToUnit(callback: (() => void) | undefined): void;
    setOnNavigateToBuilding(callback: (() => void) | undefined): void;
    // The image selected in the Images tab — shared so entity tabs can jump to
    // it (navigateToImage) and the Image Preview flyout can read/drive it.
    selectedImageKey: string | undefined;
    setSelectedImageKey(value: string | undefined): void;
    navigateToImage(key: string): void;
    setOnNavigateToImage(callback: (() => void) | undefined): void;
    // Fresh-ref focus targets for the Tiles / Resources tabs and the metadata
    // editors (Attributes / Tech / City Rewards) — same fresh-{id}/{uid} pattern
    // as focusedUnit/focusedBuilding so consumer effects re-fire on repeat nav.
    focusedTile: { id: string } | undefined;
    focusedResource: { id: string } | undefined;
    focusedMetadata: { uid: string } | undefined;
    setOnNavigateToTile(callback: (() => void) | undefined): void;
    setOnNavigateToResource(callback: (() => void) | undefined): void;
    // The metadata callback receives the collection so the editor can pick the
    // destination tab (Attributes / Tech / City Rewards).
    setOnNavigateToMetadata(callback: ((collection: MetadataCollection) => void) | undefined): void;
    // Unified dispatcher: all Ctrl/Cmd-click navigation routes through here.
    navigateTo(target: NavTarget): void;
    confirmDelete(name: string, category?: string): Promise<boolean>;
    confirm(prompt: ConfirmPrompt): Promise<boolean>;
    choose(prompt: ChoicePrompt): Promise<string | undefined>;
}

const AppContext = createContext<AppContextData>({} as AppContextData);

export function AppProvider(props: AppProviderProps) {
    const {branches, branch, state,
        setBranches, setBranch, clearHistory, executeCommand, refreshBranches, refreshState,
        setReadOnly, setEditBlockedNotifier} = useIntegrator();

    const [authenticated, setAuthenticated] = useState<boolean>(false);
    const [playFabId, setPlayFabId] = useState<string>('');
    const [displayName, setDisplayName] = useState<string>('');
    const [accessLevel, setAccessLevel] = useState<AccessLevel>(AccessLevel.None);
    // Drive the integrator's central read-only guard from the current role:
    // Viewer (and None) can view but not edit, so every user-initiated command
    // is rejected there. Backs up the hidden edit buttons — see useAccess.
    useEffect(() => {
        setReadOnly(accessLevel < AccessLevel.Guest);
    }, [accessLevel, setReadOnly]);
    const [loading, setLoading] = useState<boolean>(false);
    const [markdownFile, setMarkdownFile] = useState<string | undefined>(undefined);
    const [metadataValue, setMetadataValue] = useState<AddMetadataData | undefined>(undefined);
    // Persisted across reloads. Default OFF — designer-friendly view; toggle
    // on for code-aware work where the metadata id matters.
    const SHOW_IDS_KEY = 'rift.editor.showIds';
    const [showIds, _setShowIds] = useState<boolean>(
        () => localStorage.getItem(SHOW_IDS_KEY) === 'true'
    );
    const setShowIds = (value: boolean) => {
        localStorage.setItem(SHOW_IDS_KEY, String(value));
        _setShowIds(value);
    };
    const [viewJson, setViewJson] = useState<boolean>(false);
    const [viewBranch, setViewBranch] = useState<boolean>(false);
    const [notification, setNotification] = useState<NotificationData | undefined>(undefined);
    // Surface a toast when a read-only role's edit is blocked by the integrator's
    // central guard (e.g. a not-yet-hidden long-tail button). Registered once;
    // setNotification is a stable setter. Placed after setNotification's
    // declaration so it isn't referenced before initialization.
    useEffect(() => {
        setEditBlockedNotifier(() => setNotification({
            title: 'Read-only access',
            message: 'Your account can view this data but not edit it.',
        }));
        return () => setEditBlockedNotifier(undefined);
    }, [setEditBlockedNotifier, setNotification]);
    const [localizationFocusKey, setLocalizationFocusKey] = useState<string | undefined>(undefined);
    const navigateToLocalizationRef = useRef<((key: string) => void) | undefined>(undefined);

    // The attribute manifest is the code's schema (branch-independent), so it's
    // fetched once on mount. It's optional: if the fetch fails the editor simply
    // falls back to today's free-form attribute editing.
    //
    // Served by the backend's getAttributeManifest endpoint, which reads the
    // manifest blob the Unity client uploaded via "Rift / Export Attribute
    // Manifest". cache: 'no-store' so each page refresh re-fetches — there's
    // no version stamp, the manifest is always "latest".
    const [attributeManifest, setAttributeManifest] = useState<AttributeManifest | undefined>(undefined);
    useEffect(() => {
        fetch(`${apiBindings.api}getAttributeManifest`, {cache: 'no-store'})
            .then(r => r.ok ? r.json() : Promise.reject(r.status))
            .then((raw: string | AttributeManifest) => {
                // The backend wraps the manifest string in an OkObjectResult
                // (matching how getRemoteConstants returns its file content),
                // so the parsed body is a JSON-stringified manifest. Decode
                // once more to get the actual object.
                const m: AttributeManifest = typeof raw === 'string' ? JSON.parse(raw) : raw;
                setAttributeManifest(m);
            })
            .catch(() => setAttributeManifest(undefined));
    }, []);

    // Track whether the Ctrl/Cmd modifier is held, as a body class, so CSS can
    // give navigable references (data-navref) a link cursor + styling on hover
    // only while the modifier is down (the Ctrl/Cmd-click navigation affordance).
    useEffect(() => {
        const sync = (e: KeyboardEvent) => document.body.classList.toggle('modifier-nav', e.ctrlKey || e.metaKey);
        const clear = () => document.body.classList.remove('modifier-nav');
        window.addEventListener('keydown', sync);
        window.addEventListener('keyup', sync);
        window.addEventListener('blur', clear);
        return () => {
            window.removeEventListener('keydown', sync);
            window.removeEventListener('keyup', sync);
            window.removeEventListener('blur', clear);
            clear();
        };
    }, []);

    function navigateToLocalization(key: string) {
        // Auto-create missing localization entry (some attributes were never added)
        if (state?.localization?.['en'] && !(key in state.localization['en'])) {
            executeCommand(new ChangeLocalizationValuesCommand(key, key));
        }
        setLocalizationFocusKey(key);
        setNotification(undefined);
        if (navigateToLocalizationRef.current) navigateToLocalizationRef.current(key);
    }

    // Scroll + green-highlight an EXISTING localization row — the mechanism the
    // per-field "A" button uses (via localizationFocusKey) — without
    // navigateToLocalization's auto-create or tab switch. For a key that already
    // exists, e.g. one just added on the Localization tab.
    function focusLocalizationKey(key: string) {
        setLocalizationFocusKey(key);
    }

    function setOnNavigateToLocalization(callback: ((key: string) => void) | undefined) {
        navigateToLocalizationRef.current = callback;
    }

    const [focusedUnit, setFocusedUnit] = useState<{ id: string } | undefined>(undefined);
    const [focusedBuilding, setFocusedBuilding] = useState<{ id: string } | undefined>(undefined);
    const navigateToUnitRef = useRef<(() => void) | undefined>(undefined);
    const navigateToBuildingRef = useRef<(() => void) | undefined>(undefined);

    function navigateToUnit(id: string) {
        setFocusedUnit({ id });
        if (navigateToUnitRef.current) navigateToUnitRef.current();
    }

    function navigateToBuilding(id: string) {
        setFocusedBuilding({ id });
        if (navigateToBuildingRef.current) navigateToBuildingRef.current();
    }

    function setOnNavigateToUnit(callback: (() => void) | undefined) {
        navigateToUnitRef.current = callback;
    }

    function setOnNavigateToBuilding(callback: (() => void) | undefined) {
        navigateToBuildingRef.current = callback;
    }

    const [selectedImageKey, setSelectedImageKey] = useState<string | undefined>(undefined);
    const navigateToImageRef = useRef<(() => void) | undefined>(undefined);
    function navigateToImage(key: string) {
        setSelectedImageKey(key);
        if (navigateToImageRef.current) navigateToImageRef.current();
    }
    function setOnNavigateToImage(callback: (() => void) | undefined) {
        navigateToImageRef.current = callback;
    }

    const [focusedTile, setFocusedTile] = useState<{ id: string } | undefined>(undefined);
    const [focusedResource, setFocusedResource] = useState<{ id: string } | undefined>(undefined);
    const [focusedMetadata, setFocusedMetadata] = useState<{ uid: string } | undefined>(undefined);
    const navigateToTileRef = useRef<(() => void) | undefined>(undefined);
    const navigateToResourceRef = useRef<(() => void) | undefined>(undefined);
    const navigateToMetadataRef = useRef<((collection: MetadataCollection) => void) | undefined>(undefined);

    function navigateToTile(id: string) {
        setFocusedTile({ id });
        if (navigateToTileRef.current) navigateToTileRef.current();
    }
    function navigateToResource(id: string) {
        setFocusedResource({ id });
        if (navigateToResourceRef.current) navigateToResourceRef.current();
    }
    function navigateToMetadata(uid: string) {
        setFocusedMetadata({ uid });
        const found = findMetadata(state, uid);
        if (found && navigateToMetadataRef.current) navigateToMetadataRef.current(found.collection);
    }
    function setOnNavigateToTile(callback: (() => void) | undefined) { navigateToTileRef.current = callback; }
    function setOnNavigateToResource(callback: (() => void) | undefined) { navigateToResourceRef.current = callback; }
    function setOnNavigateToMetadata(callback: ((collection: MetadataCollection) => void) | undefined) { navigateToMetadataRef.current = callback; }

    // Unified Ctrl/Cmd-click dispatcher — routes a NavTarget to the per-kind
    // navigate helper.
    function navigateTo(target: NavTarget) {
        switch (target.kind) {
            case 'unit': navigateToUnit(target.id); break;
            case 'building': navigateToBuilding(target.id); break;
            case 'tile': navigateToTile(target.id); break;
            case 'resource': navigateToResource(target.id); break;
            case 'metadata': navigateToMetadata(target.uid); break;
        }
    }

    const confirmDeleteResolveRef = useRef<((confirmed: boolean) => void) | undefined>(undefined);
    const [confirmDeletePrompt, setConfirmDeletePrompt] = useState<{name: string; category?: string} | undefined>(undefined);

    function confirmDelete(name: string, category?: string): Promise<boolean> {
        return new Promise(resolve => {
            confirmDeleteResolveRef.current = resolve;
            setConfirmDeletePrompt({name, category});
        });
    }

    function onConfirmDeleteResult(confirmed: boolean) {
        setConfirmDeletePrompt(undefined);
        if (confirmDeleteResolveRef.current) {
            confirmDeleteResolveRef.current(confirmed);
            confirmDeleteResolveRef.current = undefined;
        }
    }

    const confirmResolveRef = useRef<((confirmed: boolean) => void) | undefined>(undefined);
    const [confirmPrompt, setConfirmPrompt] = useState<ConfirmPrompt | undefined>(undefined);

    function confirm(prompt: ConfirmPrompt): Promise<boolean> {
        return new Promise(resolve => {
            confirmResolveRef.current = resolve;
            setConfirmPrompt(prompt);
        });
    }

    function onConfirmResult(confirmed: boolean) {
        setConfirmPrompt(undefined);
        if (confirmResolveRef.current) {
            confirmResolveRef.current(confirmed);
            confirmResolveRef.current = undefined;
        }
    }

    const chooseResolveRef = useRef<((value: string | undefined) => void) | undefined>(undefined);
    const [choicePrompt, setChoicePrompt] = useState<ChoicePrompt | undefined>(undefined);

    function choose(prompt: ChoicePrompt): Promise<string | undefined> {
        return new Promise(resolve => {
            chooseResolveRef.current = resolve;
            setChoicePrompt(prompt);
        });
    }

    function onChooseResult(value: string | undefined) {
        setChoicePrompt(undefined);
        if (chooseResolveRef.current) {
            chooseResolveRef.current(value);
            chooseResolveRef.current = undefined;
        }
    }

    const [unit, setUnit] = useState<string | undefined>(undefined);

    // Probe the session cookie on load. The httpOnly cookie can't be read by JS,
    // so we ask the backend (/me) whether it's valid and at what access level.
    async function checkSession(): Promise<boolean> {
        const result = await get(`${apiBindings.api}me`, undefined);
        if (!result.success) {
            setAuthenticated(false);
            setAccessLevel(AccessLevel.None);
            return false;
        }

        setAccessLevel(result.data.accessLevel as AccessLevel);
        setPlayFabId((result.data.playFabId as string) ?? '');
        setDisplayName((result.data.displayName as string) ?? '');
        // /me re-mints the token on every probe (sliding session) — keep the
        // bearer fallback in sync so Safari/staging/local roll forward too.
        setSessionToken(result.data.token as string | undefined);
        // Load editor data BEFORE flipping `authenticated` — that flag opens the
        // EditorView, which renders against `state` immediately.
        await refreshState(branch);
        setAuthenticated(true);
        return true;
    }

    // Exchange PlayFab email/password for an editor session cookie (set httpOnly
    // by the backend). The cookie — not any value held here — authorises requests.
    async function login(email: string, password: string): Promise<boolean> {
        const result = await post(`${apiBindings.api}login`, JSON.stringify({email, password}), undefined);
        if (!result.success) {
            setNotification({
                title: 'Login Failed',
                message: 'Incorrect email/password, or this account has no editor access.'
            });

            setAuthenticated(false);
            setAccessLevel(AccessLevel.None);
            return false;
        }

        setAccessLevel(result.data.accessLevel as AccessLevel);
        setPlayFabId((result.data.playFabId as string) ?? '');
        setDisplayName((result.data.displayName as string) ?? '');
        // Keep the JWT for the bearer fallback — Safari discards the
        // cross-site session cookie, so this is its only session carrier.
        setSessionToken(result.data.token as string | undefined);
        // Load editor data BEFORE flipping `authenticated` — that flag opens the
        // EditorView, which renders against `state` immediately.
        await refreshState(branch);
        setAuthenticated(true);
        return true;
    }

    // Self-service account creation. The backend creates the PlayFab account,
    // grants the default Tester role, and mints the session in one call — so on
    // success we're authenticated exactly as if login() had run.
    async function register(name: string, email: string, password: string): Promise<boolean> {
        const result = await post(`${apiBindings.api}register`, JSON.stringify({name, email, password}), undefined);
        if (!result.success) {
            setNotification({
                title: 'Registration Failed',
                message: (result.data?.message as string) ?? 'Could not create the account. Please try again.'
            });

            setAuthenticated(false);
            setAccessLevel(AccessLevel.None);
            return false;
        }

        setAccessLevel(result.data.accessLevel as AccessLevel);
        setPlayFabId((result.data.playFabId as string) ?? '');
        setDisplayName((result.data.displayName as string) ?? '');
        setSessionToken(result.data.token as string | undefined);
        // Load editor data BEFORE flipping `authenticated` — that flag opens the
        // EditorView, which renders against `state` immediately.
        await refreshState(branch);
        setAuthenticated(true);
        return true;
    }

    function logout(): void {
        // Clear the cookie server-side (httpOnly → JS can't), then reset local state.
        post(`${apiBindings.api}logout`, '', undefined);
        setSessionToken(undefined);
        setAuthenticated(false);
        setAccessLevel(AccessLevel.None);
        setPlayFabId('');
        setDisplayName('');
    }

    async function exportRemoteConstants(prod: boolean): Promise<boolean> {
        return await exportRemoteConstantsByBranch(branch, prod);
    }

    function getInactiveReferenceWarnings(data: RemoteConstantsModel): string[] {
        const loc = data.localization?.['en'] ?? {};
        const name = (id: string) => loc[id] ?? id;

        const inactiveBuildingIds = new Set(
            data.buildings.filter(b => !b.active).map(b => b.id)
        );
        const inactiveUnitIds = new Set(
            data.units.filter(u => !u.active).map(u => u.id)
        );

        const warnings: string[] = [];

        for (const reward of data.rewards) {
            const buildingId = reward.entities?.buildingId;
            const unitId = reward.entities?.unitId;
            if (buildingId && inactiveBuildingIds.has(buildingId))
                warnings.push(`Reward "${name(reward.uid)}" references inactive building "${name(buildingId)}"`);
            if (unitId && inactiveUnitIds.has(unitId))
                warnings.push(`Reward "${name(reward.uid)}" references inactive unit "${name(unitId)}"`);
        }

        for (const node of data.techTree) {
            for (const id of node.unlocks ?? []) {
                if (inactiveBuildingIds.has(id))
                    warnings.push(`Tech "${name(node.id)}" unlocks inactive building "${name(id)}"`);
                if (inactiveUnitIds.has(id))
                    warnings.push(`Tech "${name(node.id)}" unlocks inactive unit "${name(id)}"`);
            }
        }

        warnings.push(...getUnitAttributeDependencyWarnings(data, name,
            id => getAttributeManifestEntry(id, MetadataCollection.Units)?.Requires ?? []));

        return warnings;
    }

    // Tries getAllBranches up to `maxAttempts` times, with a small backoff
    // between attempts. Returns the branch list on success, or null if every
    // attempt failed. Used by the export concurrency check so a transient
    // network blip doesn't silently bypass the conflict warning.
    async function fetchBranchesWithRetry(
        maxAttempts: number = 3,
        delayMs: number = 500,
    ): Promise<{ name: string; versionNumber: number }[] | null> {
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            const result = await get(`${apiBindings.api}getAllBranches`, undefined);
            if (result.success) return result.data as { name: string; versionNumber: number }[];
            if (attempt < maxAttempts)
                await new Promise(resolve => setTimeout(resolve, delayMs * attempt));
        }
        return null;
    }

    async function exportRemoteConstantsByBranch(branch: string, prod: boolean, baselineVersionOverride?: number): Promise<boolean> {
        const clientVersion = process.env.REACT_APP_VERSION ?? '';
        const warnings = getInactiveReferenceWarnings(state);

        setLoading(true);

        // PixelMultiplier is a computed output, not an authored field: bake it
        // from each image's `scale` and natural pixel size so the client renders
        // sprites at the editor's WYSIWYG size with no client changes. Done on a
        // clone — the live editing state keeps its source values. Blocks the
        // export if any image's size can't be resolved (vs shipping a wrong one).
        let exportImages: typeof state.images;
        try {
            exportImages = await bakeImagePixelMultipliers(state.images);
        } catch (e) {
            setLoading(false);
            setNotification({
                title: 'Export blocked',
                message: e instanceof Error ? e.message : 'Could not compute image sizes.'
            });
            return false;
        }
        // Export-time backstop: clamp every constrained field to its manifest
        // range on a deep clone (live editing state is untouched), so shipped data
        // is guaranteed in-range even for inputs not yet wired to a constraint or
        // values that were pasted/imported.
        const exportState = JSON.parse(JSON.stringify({...state, images: exportImages}));
        applyFieldConstraints(exportState, attributeManifest?.FieldConstraints);
        applyParamConstraints(exportState, attributeManifest);
        const payload = JSON.stringify(exportState);

        // Concurrency check: someone else may have exported to this branch since
        // we last refreshed. Compare our locally-known version against the live
        // server-side version and warn before clobbering.
        //
        // baselineVersionOverride lets callers like addBranch pass in the version
        // they just created the branch at — the React `branches` state hasn't
        // flushed yet inside this closure, so the lookup below would otherwise
        // see the branch as missing and fire a spurious "your v-1 vs live v0"
        // warning on every brand-new branch's auto-export.
        const localKnownVersion = baselineVersionOverride ?? branches.find(b => b.name === branch)?.versionNumber ?? -1;
        const serverBranches = await fetchBranchesWithRetry();
        if (serverBranches === null) {
            // Couldn't reach the server even after retries. Don't silently
            // proceed — the user might be about to clobber someone's work.
            setLoading(false);
            const proceed = await confirm({
                title: 'Could not verify latest branch version',
                body: (
                    <div className={'layout vertical gap'}>
                        <p>We couldn't fetch the latest branch state to check for conflicts (after several attempts).</p>
                        <p>If anyone else has exported to <strong>{branch}</strong> since you last refreshed, continuing will overwrite their changes without warning.</p>
                    </div>
                ),
                confirmText: 'Export anyway',
                danger: true,
            });
            if (!proceed) return false;
            setLoading(true);
        } else {
            const serverVersion = serverBranches.find(b => b.name === branch)?.versionNumber ?? -1;

            if (serverVersion > localKnownVersion) {
                setLoading(false);
                const proceed = await confirm({
                    title: 'Warning — your export would overwrite changes from other users',
                    body: (
                        <div className={'layout vertical gap'}>
                            <p>You are based on <strong>{branch} v{localKnownVersion}</strong>, but the live branch is now at <strong>v{serverVersion}</strong>.</p>
                            <p>Continuing will export your changes over the top of versions {localKnownVersion + 1}{serverVersion > localKnownVersion + 1 ? `–${serverVersion}` : ''}.</p>
                        </div>
                    ),
                    confirmText: 'Export anyway',
                    danger: true,
                });
                // If they cancel, leave local branches state as-is so the
                // bottom bar continues to reflect that they're behind. The
                // refreshBranches() call after a successful export below
                // handles the proceed case.
                if (!proceed) return false;
                setLoading(true);
            } else {
                // No conflict — safe to sync local state with server now.
                setBranches(serverBranches);
            }
        }

        const response = await post(`${apiBindings.api}setRemoteConstants?branch=${branch}&prod=${prod}&clientVersion=${clientVersion}`, payload, undefined);
        await refreshBranches();

        setLoading(false);

        if (response.success) {
            clearHistory();
            setNotification({
                title: 'Export Complete',
                message: 'You can refresh the game content now.',
                warnings: warnings.length > 0 ? warnings : undefined,
            });
        } else {
            setNotification({
                title: 'Export Failed',
                message: response.data?.message
            });
        }

        return response.success;
    }

    async function addBranch(branch: string): Promise<boolean> {
        setLoading(true);
        const response = await get(`${apiBindings.api}addBranch?branch=${branch}`, undefined);
        setLoading(false);

        if (!response.success) {
            setNotification({
                title: 'Failed',
                message: 'Failed to create a new branch.'
            });
            return false;
        }

        const newBranches = [...branches];
        newBranches.push({name: branch, versionNumber: 0});

        setBranches(newBranches);
        setBranch(branch);
        // Seed the new branch's content. Publishing to prod is Admin-only, so a
        // Tester's new branch is seeded to dev/test only (an Admin can later
        // export it to staging/live); an Admin's is seeded straight to prod, as
        // before. Pass baseline=0 explicitly: the backend created the branch at
        // v0 and the setBranches state update above hasn't flushed in this closure.
        await exportRemoteConstantsByBranch(branch, accessLevel >= AccessLevel.Admin, 0);
        return true;
    }

    async function removeBranch(removeBranch: string): Promise<boolean> {
        const response = await get(`${apiBindings.api}removeBranch?branch=${removeBranch}`, undefined);
        if (!response.success) {
            return false;
        }

        const newBranches = branches.filter(b => b.name !== removeBranch);
        setBranches(newBranches);

        if (removeBranch === branch) {
            setBranch(newBranches[0].name);
        }

        return true;
    }

    // Isolate an image's subject via the backend remove.bg proxy. Returns the
    // transparent PNG (the caller previews it and, on accept, uploads it as a
    // new version via uploadFile). The response is binary, so this doesn't use
    // the JSON http.client helpers.
    async function removeBackground(id: string): Promise<Blob | undefined> {
        const image = state.images[id];
        if (!image?.fileName) return undefined;
        setLoading(true);
        try {
            // Raw fetch (binary response), so it must attach the session
            // bearer itself — http.client does this for every JSON call, and
            // without it a cross-site setup (localhost dev, Safari) sends no
            // identity at all and the backend rejects the edit.
            const headers: HeadersInit = {};
            const bearer = getSessionToken();
            if (bearer !== undefined) headers['Authorization'] = 'Bearer ' + bearer;
            const response = await fetch(
                `${apiBindings.api}removeBackground?fileName=${encodeURIComponent(image.fileName)}`,
                {credentials: 'include', headers},
            );
            if (!response.ok) {
                let message = 'Background removal failed.';
                try { message = (await response.json())?.message ?? message; } catch (_) { /* non-JSON */ }
                setNotification({title: 'Background removal failed', message});
                return undefined;
            }
            return await response.blob();
        } catch (e) {
            setNotification({title: 'Background removal failed', message: e instanceof Error ? e.message : String(e)});
            return undefined;
        } finally {
            setLoading(false);
        }
    }

    async function uploadFile(id: string, file: File): Promise<boolean> {
        const formData = new FormData();
        formData.append("file", file);

        const result = await postFile(`${apiBindings.api}uploadFile?id=${id}`, formData, undefined);
        if (!result.success) {
            setNotification({
                title: 'Failed to upload file.',
                message: result.data?.message
            })
            return false;
        }

        const fileName = result.data.fileName as string;
        const fileExtension = fileName.split('.').pop() ?? '';

        // Capture the source image's natural dimensions now (locally), so the
        // export PPU bake is resolution-independent and never has to re-fetch.
        const dimensions = fileExtension === 'png' ? await readImageDimensions(file) : undefined;
        // ...and where its subject sits, so the client can frame it as a UI icon
        // without scanning the alpha channel at runtime. Recomputed on every upload
        // rather than carried over: new art means new framing, and stale bounds are
        // worse than none (the client would crop to the wrong box instead of
        // deriving the right one).
        const trim = fileExtension === 'png' ? await readImageTrim(file) : undefined;

        executeCommand(new ChangeConstantsCommand(data => {
            switch (fileExtension) {
                case 'png':
                    if (id in data.images) {
                        const curImageData = data.images[id];
                        const oldImageFileName = curImageData.fileName;
                        curImageData.fileName = fileName;
                        if (dimensions) {
                            curImageData.srcWidth = dimensions.width;
                            curImageData.srcHeight = dimensions.height;
                            // Keep the authored scale; resize PPU to the new file.
                            curImageData.pixelMultiplier = computePixelMultiplier(dimensions.width, curImageData.scale);
                        }
                        // Assign either way: dropping the key when the new art has
                        // no readable subject is deliberate, so the client derives
                        // the box rather than reusing the previous art's.
                        if (trim) curImageData.trim = trim;
                        else delete curImageData.trim;
                        data.archive.push({
                            fileName: oldImageFileName,
                            key: id
                        });
                    } else {
                        data.images[id] = {
                            fileName: fileName,
                            // PPU is derived from the image's width (scale 1 by
                            // default); 800 is only a last resort if the file
                            // couldn't be decoded — the export will then block.
                            pixelMultiplier: dimensions ? computePixelMultiplier(dimensions.width, undefined) : 800,
                            offsetX: 0.5,
                            offsetY: 0.5,
                            srcWidth: dimensions?.width,
                            srcHeight: dimensions?.height,
                            trim: trim
                        }
                    }
                    break;
                case 'wav':
                    if (id in data.sounds) {
                        const curSoundData = data.sounds[id];
                        const oldSoundFileName = curSoundData.fileName;
                        curSoundData.fileName = fileName;
                        data.archive.push({
                            fileName: oldSoundFileName,
                            key: id
                        });
                    } else {
                        data.sounds[id] = {
                            active: true,
                            fileName: fileName,
                            description: '',
                            type: SoundTypes[0],
                            startDelay: 0,
                            delay: 0,
                            volume: 1,
                            channels: 1,
                            frequency: 44100
                        }
                    }
                    break;
            }
        }));

        return true;
    }

    // Masks reuse the plain uploadFile endpoint — it only stores a blob under a
    // fresh GUID and hands the name back, so nothing server-side needs to know
    // the difference. What differs is where the name is written: into the
    // image's own `mask` record rather than over its `fileName`.
    async function uploadMask(
        id: string,
        file: File,
        settings: Omit<ImageMaskMetadata, 'fileName'>,
    ): Promise<boolean> {
        if (state.images === undefined || !(id in state.images)) {
            setNotification({
                title: 'Failed to attach mask.',
                message: `There is no image at '${id}' to attach it to.`,
            });
            return false;
        }

        const formData = new FormData();
        formData.append("file", file);

        const result = await postFile(`${apiBindings.api}uploadFile?id=${id}`, formData, undefined);
        if (!result.success) {
            setNotification({
                title: 'Failed to upload mask.',
                message: result.data?.message,
            });
            return false;
        }

        const fileName = result.data.fileName as string;

        executeCommand(new ChangeConstantsCommand(data => {
            const image = data.images[id];
            if (image === undefined) return;

            const displaced = image.mask?.fileName;
            image.mask = {...settings, fileName};

            // Same courtesy the image path extends to a replaced asset: the old
            // blob stays recoverable rather than being orphaned in storage.
            if (displaced !== undefined && displaced.length > 0 && displaced !== fileName) {
                data.archive.push({fileName: displaced, key: id});
            }
        }));

        return true;
    }

    function removeMask(id: string): void {
        executeCommand(new ChangeConstantsCommand(data => {
            const image = data.images?.[id];
            const displaced = image?.mask?.fileName;
            if (image === undefined || image.mask === undefined) return;

            delete image.mask;
            if (displaced !== undefined && displaced.length > 0) {
                data.archive.push({fileName: displaced, key: id});
            }
        }));
    }

    function getMaskUrl(id: string): string | undefined {
        const fileName = state.images?.[id]?.mask?.fileName;
        if (fileName === undefined || fileName.length === 0) return undefined;

        return `${apiBindings.images}${fileName}`;
    }

    function getImageUrl(id: string): string {
        // Resolve a key through its image pack to the image it displays —
        // mirrors the client's GetEntityImageKey. Buildings start at runtime
        // level `startLevel` and their column 0 is a hidden dead slot, so read
        // the start row; everything else reads the top-left (level 0). The pack
        // top-left IS the entity's image — there is no separate "entity image".
        const pack = state.imagePacks?.[id];
        if (pack && pack.length > 0) {
            const building = state.buildings?.find(b => b.id === id);
            const row = building
                ? Math.max(0, Math.min(pack.length - 1, building.startLevel))
                : 0;
            const target = pack[row]?.[0];
            if (target && target !== id && state.images && target in state.images) {
                id = target;
            }
        }

        if (state.images === undefined || !(id in state.images)) {
            return '/assets/default.png';
        }

        const data = state.images[id];
        let imageUrl = '';

        if (typeof(data) === 'string') {
            imageUrl = `${apiBindings.images}${data}`;
        } else if (data.fileName.length > 0) {
            imageUrl = `${apiBindings.images}${data.fileName}`;
        } else {
            return '/assets/default.png';
        }

        return imageUrl;
    }

    function getSoundUrl(id: string): string | undefined {
        if (id.endsWith('.wav')) {
            return `${apiBindings.images}${id}`;
        }

        if (state.sounds === undefined || !(id in state.sounds)) {
            return undefined;
        }

        const data = state.sounds[id];
        let imageUrl = '';

        if (typeof(data) === 'string') {
            imageUrl = `${apiBindings.images}${data}`;
        } else if (data.fileName.length > 0) {
            imageUrl = `${apiBindings.images}${data.fileName}`;
        } else {
            return undefined;
        }

        return imageUrl;
    }

    // Resolve the shared type-level description key for a metadata instance:
    // prefer the namespaced key once it exists in the data (post-migration or
    // freshly seeded), else fall back to the legacy bare key. Used for both
    // display reads and as the edit target, so editing always lands on the live
    // key and the editor transitions per-key as the migration populates them.
    //
    // A per-instance "<uid>.description" layer was tried in front of this and
    // removed — see LocalizationManager.ResolveTypeDescriptionKey: rewards carry
    // seeded placeholder copies written before anything read them, so preferring
    // the instance key buried the real text.
    function getTypeDescriptionKey(uid: string, typeId: string): string {
        const namespaced = namespacedTypeDescriptionKey(uid, typeId);
        const en = state?.localization?.['en'];
        if (en && namespaced in en) return namespaced;
        return legacyTypeDescriptionKey(typeId);
    }

    function getLocalizationValue(key: string): string {
        if (state?.localization === undefined) {
            return missingLocalization(key);
        }

        const language = 'en';
        if (language in state.localization && key in state.localization[language]) {
            return state.localization[language][key];
        }

        return missingLocalization(key);
    }

    function getEntityOptions(): { label: string; options: string[] }[] {
        // Sort each group alphabetically by localized name. Empty groups
        // (e.g. branches with no tech passives) are dropped so the dropdown
        // doesn't show an empty section header.
        const byName = (a: string, b: string) =>
            getLocalizationValue(a).localeCompare(getLocalizationValue(b));
        const groups = [
            {label: 'Units', options: state.units.map(u => u.id).sort(byName)},
            {label: 'Buildings', options: state.buildings.map(b => b.id).sort(byName)},
            {label: 'Tiles', options: state.tiles.map(t => t.id).sort(byName)},
            {label: 'Resources', options: state.resources.map(r => r.id).sort(byName)},
            {label: 'Tech', options: (state.techTree ?? []).map(n => n.id).sort(byName)},
            {label: 'Tech Passives', options: (state.techPassiveAttributes ?? []).map(m => m.uid).sort(byName)},
        ];
        return groups.filter(g => g.options.length > 0);
    }

    function getFieldConstraint(key: string): FieldConstraint | undefined {
        return attributeManifest?.FieldConstraints?.[key];
    }

    function getAttributeManifestEntry(id: string, collection: MetadataCollection): AttributeManifestEntry | undefined {
        if (!attributeManifest) return undefined;
        const list = collection === MetadataCollection.Units ? attributeManifest.UnitAttributes
            : collection === MetadataCollection.Buildings ? attributeManifest.BuildingAttributes
                : collection === MetadataCollection.Rewards ? attributeManifest.CityUpgrades
                    : collection === MetadataCollection.TechPassives ? attributeManifest.TechPassives
                        : collection === MetadataCollection.TechActions ? attributeManifest.TechActions
                            : undefined;
        return list?.find(e => e.Ids.includes(id));
    }

    function getAttributeManifestEntries(collection: MetadataCollection): AttributeManifestEntry[] {
        if (!attributeManifest) return [];
        if (collection === MetadataCollection.Units) return attributeManifest.UnitAttributes;
        if (collection === MetadataCollection.Buildings) return attributeManifest.BuildingAttributes;
        // ?? [] guards a stale manifest.json predating the CityUpgrades / TechPassives / TechActions sections.
        if (collection === MetadataCollection.Rewards) return attributeManifest.CityUpgrades ?? [];
        if (collection === MetadataCollection.TechPassives) return attributeManifest.TechPassives ?? [];
        if (collection === MetadataCollection.TechActions) return attributeManifest.TechActions ?? [];
        return [];
    }

    function isHiddenUnitAttribute(id: string): boolean {
        if (!attributeManifest) return false;
        return attributeManifest.UnitAttributes.some(e => e.Hidden && e.Ids.includes(id));
    }

    // Single-group option list scoped to one entity kind, for manifest-driven
    // entity pickers. Collectables have no dedicated catalogue — they're the
    // inventory resources.
    function getEntityOptionsForKind(kind: AttributeEntityKind): { label: string; options: string[] }[] {
        const byName = (a: string, b: string) =>
            getLocalizationValue(a).localeCompare(getLocalizationValue(b));
        switch (kind) {
            case 'unit':
                return [{label: 'Units', options: state.units.map(u => u.id).sort(byName)}];
            case 'building':
                return [{label: 'Buildings', options: state.buildings.map(b => b.id).sort(byName)}];
            case 'tech':
                return [{label: 'Tech', options: (state.techTree ?? []).map(n => n.id).sort(byName)}];
            case 'collectable':
                return [{label: 'Resources', options: state.resources.map(r => r.id).sort(byName)}];
            case 'techaction':
                // Tech actions are referenced by uid (multiple instances can share
                // an id), matching what tech-node unlocks and HasTechUnlock use.
                return [{label: 'Tech Actions', options: (state.techActionAttributes ?? []).map(m => m.uid).sort(byName)}];
            default:
                return getEntityOptions();
        }
    }

    return (
        <AppContext.Provider
            value={{
                authenticated,
                playFabId,
                displayName,
                accessLevel,
                loading, setLoading,
                markdownFile, setMarkdownFile,
                metadataValue, setMetadataValue,
                showIds, setShowIds,
                viewJson, setViewJson,
                viewBranch, setViewBranch,
                notification, setNotification,
                unit, setUnit,
                login,
                register,
                checkSession,
                logout,
                exportRemoteConstants,
                addBranch,
                removeBranch,
                uploadFile,
                uploadMask,
                removeMask,
                getMaskUrl,
                removeBackground,
                getImageUrl,
                getSoundUrl,
                getLocalizationValue,
                getTypeDescriptionKey,
                getEntityOptions,
                getAttributeManifestEntry,
                getFieldConstraint,
                isHiddenUnitAttribute,
                attributeManifest,
                getAttributeManifestEntries,
                getEntityOptionsForKind,
                localizationFocusKey,
                navigateToLocalization,
                focusLocalizationKey,
                setOnNavigateToLocalization,
                focusedUnit,
                focusedBuilding,
                navigateToUnit,
                navigateToBuilding,
                setOnNavigateToUnit,
                setOnNavigateToBuilding,
                selectedImageKey,
                setSelectedImageKey,
                navigateToImage,
                setOnNavigateToImage,
                focusedTile,
                focusedResource,
                focusedMetadata,
                setOnNavigateToTile,
                setOnNavigateToResource,
                setOnNavigateToMetadata,
                navigateTo,
                confirmDelete,
                confirm,
                choose,
            }}>
            {props.children}
            {confirmDeletePrompt !== undefined && (
                <div className={'layout vertical'} onClick={() => onConfirmDeleteResult(false)}
                     style={{
                         position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                         backgroundColor: 'rgba(0, 0, 0, 0.6)',
                         alignItems: 'center', justifyContent: 'center',
                         display: 'flex', zIndex: 2000
                     }}>
                    <div className={'layout vertical gap box'} style={{padding: 20, gap: 15, width: 350}}
                         onClick={e => e.stopPropagation()}>
                        <p className={'text header'}>Confirm: delete{confirmDeletePrompt.category ? ` ${confirmDeletePrompt.category}` : ''}</p>
                        <p>Really delete <strong>{confirmDeletePrompt.name}</strong>?</p>
                        <div className={'layout horizontal gap'} style={{justifyContent: 'flex-end'}}>
                            <button onClick={() => onConfirmDeleteResult(false)}>Cancel</button>
                            <button onClick={() => onConfirmDeleteResult(true)}
                                    style={{backgroundColor: '#aa4444', color: 'white'}}>Delete</button>
                        </div>
                    </div>
                </div>
            )}
            {confirmPrompt !== undefined && (
                <div className={'layout vertical'} onClick={() => onConfirmResult(false)}
                     style={{
                         position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                         backgroundColor: 'rgba(0, 0, 0, 0.6)',
                         alignItems: 'center', justifyContent: 'center',
                         display: 'flex', zIndex: 2000
                     }}>
                    <div className={'layout vertical gap box'} style={{padding: 20, gap: 15, width: 420}}
                         onClick={e => e.stopPropagation()}>
                        <p className={'text header'}>{confirmPrompt.title}</p>
                        <div>{confirmPrompt.body}</div>
                        <div className={'layout horizontal gap'} style={{justifyContent: 'flex-end'}}>
                            <button onClick={() => onConfirmResult(false)}>Cancel</button>
                            <button onClick={() => onConfirmResult(true)}
                                    style={confirmPrompt.danger ? {backgroundColor: '#aa4444', color: 'white'} : undefined}>
                                {confirmPrompt.confirmText}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {choicePrompt !== undefined && (
                <div className={'layout vertical'} onClick={() => onChooseResult(undefined)}
                     style={{
                         position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                         backgroundColor: 'rgba(0, 0, 0, 0.6)',
                         alignItems: 'center', justifyContent: 'center',
                         display: 'flex', zIndex: 2000
                     }}>
                    <div className={'layout vertical gap box'} style={{padding: 20, gap: 15, width: 420}}
                         onClick={e => e.stopPropagation()}>
                        <p className={'text header'}>{choicePrompt.title}</p>
                        <div>{choicePrompt.body}</div>
                        <div className={'layout horizontal gap'} style={{justifyContent: 'flex-end'}}>
                            {choicePrompt.options.map(opt => (
                                <button key={opt.value} onClick={() => onChooseResult(opt.value)}
                                        style={opt.danger ? {backgroundColor: '#aa4444', color: 'white'} : undefined}>
                                    {opt.text}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </AppContext.Provider>
    )
}

export const useApp = () => useContext(AppContext);